import React from 'react';

// ─── Утилиты ──────────────────────────────────────────────────────────────────

function getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = 'Неизвестный';
    if (/Edg\//.test(ua)) browser = 'Microsoft Edge';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua)) browser = 'Google Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';

    const versionMatch = ua.match(/(Chrome|Firefox|Safari|Edg|OPR)\/(\d+\.\d+)/);
    const version = versionMatch ? versionMatch[2] : '—';

    return `${browser} ${version}`;
}

function getDeviceInfo() {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return '📱 iPhone';
    if (/iPad/.test(ua)) return '📱 iPad';
    if (/Android/.test(ua)) {
        const model = ua.match(/Android [^;]+; ([^)]+)/)?.[1] || 'Android';
        return `📱 ${model}`;
    }
    return '🖥️ Компьютер';
}

function getOSInfo() {
    const ua = navigator.userAgent;
    if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
    if (/Windows NT 6.3/.test(ua)) return 'Windows 8.1';
    if (/Mac OS X/.test(ua)) {
        const v = ua.match(/Mac OS X ([^)]+)/)?.[1]?.replace(/_/g, '.') || '';
        return `macOS ${v}`;
    }
    if (/Android (\d+\.\d+)/.test(ua)) return `Android ${ua.match(/Android (\d+\.\d+)/)[1]}`;
    if (/iPhone OS ([^ ]+)/.test(ua)) return `iOS ${ua.match(/iPhone OS ([^ ]+)/)[1]?.replace(/_/g, '.')}`;
    if (/Linux/.test(ua)) return 'Linux';
    return 'Неизвестно';
}

function formatStack(stack) {
    if (!stack) return [];
    return stack
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .slice(0, 20);
}

function getTimestamp() {
    const now = new Date();
    return now.toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

// ─── Стили (инлайн, чтобы работали даже если CSS-файл не загрузился) ──────────

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: '#0a0a0f',
        overflowY: 'auto',
        fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
        color: '#e0e0e0',
    },
    container: {
        maxWidth: 780,
        margin: '0 auto',
        padding: '40px 24px 60px',
    },
    topBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(255,59,48,0.12)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: 6,
        padding: '6px 12px',
        fontSize: 12,
        color: '#ff3b30',
        fontWeight: 700,
        letterSpacing: '0.08em',
        marginBottom: 24,
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: '#ff3b30',
        animation: 'pulse 1.5s ease-in-out infinite',
        flexShrink: 0,
    },
    errorTitle: {
        fontSize: 'clamp(20px, 4vw, 28px)',
        fontWeight: 800,
        color: '#ffffff',
        margin: '0 0 8px',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
    },
    errorSubtitle: {
        fontSize: 14,
        color: '#888',
        margin: '0 0 32px',
        lineHeight: 1.6,
    },
    card: {
        background: '#111118',
        border: '1px solid #1e1e2e',
        borderRadius: 12,
        marginBottom: 16,
        overflow: 'hidden',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 18px',
        borderBottom: '1px solid #1e1e2e',
        background: '#0d0d14',
    },
    cardHeaderIcon: {
        fontSize: 14,
    },
    cardHeaderTitle: {
        fontSize: 11,
        fontWeight: 700,
        color: '#666',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    cardBody: {
        padding: '16px 18px',
    },
    errorMessage: {
        fontSize: 15,
        color: '#ff6b6b',
        fontWeight: 700,
        margin: 0,
        lineHeight: 1.5,
        wordBreak: 'break-word',
    },
    grid2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
    },
    infoRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    infoLabel: {
        fontSize: 10,
        color: '#555',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
    },
    infoValue: {
        fontSize: 13,
        color: '#c0c0d0',
    },
    stackLine: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '5px 0',
        borderBottom: '1px solid #15151f',
    },
    stackIndex: {
        fontSize: 10,
        color: '#444',
        width: 20,
        flexShrink: 0,
        paddingTop: 2,
    },
    stackText: {
        fontSize: 12,
        color: '#8888aa',
        wordBreak: 'break-all',
        lineHeight: 1.5,
    },
    stackTextFirst: {
        fontSize: 12,
        color: '#e0c88a',
        wordBreak: 'break-all',
        lineHeight: 1.5,
        fontWeight: 700,
    },
    componentLine: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '4px 0',
        borderBottom: '1px solid #15151f',
    },
    componentArrow: {
        fontSize: 10,
        color: '#572AC3',
        flexShrink: 0,
        paddingTop: 2,
    },
    componentText: {
        fontSize: 12,
        color: '#9999bb',
        wordBreak: 'break-all',
        lineHeight: 1.5,
    },
    buttonRow: {
        display: 'flex',
        gap: 12,
        marginTop: 28,
        flexWrap: 'wrap',
    },
    btnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: 'linear-gradient(135deg, #572AC3 0%, #8d6aec 100%)',
        color: 'white',
        border: 'none',
        borderRadius: 8,
        padding: '11px 22px',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'opacity .15s',
        letterSpacing: '0.02em',
    },
    btnSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: 'transparent',
        color: '#888',
        border: '1px solid #2a2a3a',
        borderRadius: 8,
        padding: '11px 22px',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all .15s',
        letterSpacing: '0.02em',
    },
    copySuccess: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 14px',
        background: 'rgba(31,122,31,0.15)',
        border: '1px solid rgba(31,122,31,0.3)',
        borderRadius: 6,
        fontSize: 12,
        color: '#4caf50',
        fontWeight: 700,
        marginTop: 10,
    },
    instruction: {
        fontSize: 13,
        color: '#666',
        lineHeight: 1.7,
        padding: '14px 18px',
        background: 'rgba(87,42,195,0.06)',
        borderTop: '1px solid #1e1e2e',
    },
    divider: {
        border: 'none',
        borderTop: '1px solid #1e1e2e',
        margin: '24px 0',
    },
};

// ─── CSS анимации (вставляем один раз) ────────────────────────────────────────

const globalCSS = `
@keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.8); }
}
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
}
.geb-container { animation: fadeIn 0.4s ease-out both; }
.geb-btn:hover { opacity: 0.82 !important; }
.geb-btn-sec:hover { color: #ccc !important; border-color: #444 !important; }
`;

// ─── Компонент страницы ошибки ─────────────────────────────────────────────────

class ErrorPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { copied: false, textCopied: false };
    }

    buildReportText() {
        const { error, errorInfo, meta } = this.props;
        const stack = formatStack(error?.stack);
        const compStack = (errorInfo?.componentStack || '')
            .split('\n').map(l => l.trim()).filter(Boolean).slice(0, 10);

        return [
            '═══════════════════════════════════════',
            '🚨 CRASH REPORT — измерения.рф',
            '═══════════════════════════════════════',
            '',
            `⏱ Время:       ${meta.timestamp}`,
            `📍 Страница:    ${meta.url}`,
            `🌐 Браузер:     ${meta.browser}`,
            `💻 Устройство:  ${meta.device}`,
            `🖥 ОС:          ${meta.os}`,
            `📐 Экран:       ${meta.screen}`,
            '',
            '─── ОШИБКА ─────────────────────────────',
            `${error?.name || 'Error'}: ${error?.message || 'Неизвестная ошибка'}`,
            '',
            '─── СТЕК ВЫЗОВОВ ───────────────────────',
            ...stack,
            '',
            '─── ДЕРЕВО КОМПОНЕНТОВ ─────────────────',
            ...compStack,
            '',
            '═══════════════════════════════════════',
        ].join('\n');
    }

    copyReport() {
        const text = this.buildReportText();
        navigator.clipboard?.writeText(text).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2500);
        }).catch(() => {
            // Фоллбэк для старых браузеров
            const el = document.createElement('textarea');
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2500);
        });
    }

    render() {
        const { error, errorInfo, meta, onReset } = this.props;
        const { copied } = this.state;
        const stack = formatStack(error?.stack);
        const compStack = (errorInfo?.componentStack || '')
            .split('\n').map(l => l.trim()).filter(Boolean).slice(0, 12);

        return (
            <div style={styles.overlay}>
                <style>{globalCSS}</style>
                <div style={styles.container} className="geb-container">

                    {/* Бейдж */}
                    <div style={styles.topBadge}>
                        <div style={styles.dot} />
                        КРИТИЧЕСКАЯ ОШИБКА ПРИЛОЖЕНИЯ
                    </div>

                    {/* Заголовок */}
                    <h1 style={styles.errorTitle}>Приложение упало 💥</h1>
                    <p style={styles.errorSubtitle}>
                        Сделайте скриншот этой страницы или скопируйте отчёт и отправьте разработчику.
                        Это поможет быстро найти и устранить проблему.
                    </p>

                    {/* Сообщение ошибки */}
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <span style={styles.cardHeaderIcon}>⛔</span>
                            <span style={styles.cardHeaderTitle}>Сообщение об ошибке</span>
                        </div>
                        <div style={styles.cardBody}>
                            <p style={styles.errorMessage}>
                                {error?.name && <span style={{ color: '#ff9f43' }}>{error.name}: </span>}
                                {error?.message || 'Неизвестная ошибка'}
                            </p>
                        </div>
                    </div>

                    {/* Системная информация */}
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <span style={styles.cardHeaderIcon}>🖥️</span>
                            <span style={styles.cardHeaderTitle}>Системная информация</span>
                        </div>
                        <div style={{ ...styles.cardBody }}>
                            <div style={styles.grid2}>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>⏱ Время</span>
                                    <span style={styles.infoValue}>{meta.timestamp}</span>
                                </div>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>🌐 Браузер</span>
                                    <span style={styles.infoValue}>{meta.browser}</span>
                                </div>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>💻 Устройство</span>
                                    <span style={styles.infoValue}>{meta.device}</span>
                                </div>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>🖥 ОС</span>
                                    <span style={styles.infoValue}>{meta.os}</span>
                                </div>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>📐 Экран</span>
                                    <span style={styles.infoValue}>{meta.screen}</span>
                                </div>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>📍 Страница</span>
                                    <span style={{ ...styles.infoValue, wordBreak: 'break-all', fontSize: 11 }}>{meta.url}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Стек вызовов */}
                    {stack.length > 0 && (
                        <div style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.cardHeaderIcon}>📋</span>
                                <span style={styles.cardHeaderTitle}>Стек вызовов (Stack Trace)</span>
                            </div>
                            <div style={{ ...styles.cardBody, padding: '8px 18px' }}>
                                {stack.map((line, i) => (
                                    <div key={i} style={styles.stackLine}>
                                        <span style={styles.stackIndex}>{i}</span>
                                        <span style={i === 0 ? styles.stackTextFirst : styles.stackText}>{line}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Дерево компонентов */}
                    {compStack.length > 0 && (
                        <div style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.cardHeaderIcon}>🌿</span>
                                <span style={styles.cardHeaderTitle}>Дерево React-компонентов</span>
                            </div>
                            <div style={{ ...styles.cardBody, padding: '8px 18px' }}>
                                {compStack.map((line, i) => (
                                    <div key={i} style={styles.componentLine}>
                                        <span style={styles.componentArrow}>{'›'}</span>
                                        <span style={styles.componentText}>{line}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={styles.instruction}>
                                💡 Читайте снизу вверх: последний компонент в списке — <strong style={{ color: '#c0c0d0' }}>источник ошибки</strong>
                            </div>
                        </div>
                    )}

                    <hr style={styles.divider} />

                    {/* Инструкция */}
                    <div style={{
                        ...styles.card,
                        border: '1px solid rgba(87,42,195,0.3)',
                        background: 'rgba(87,42,195,0.05)',
                    }}>
                        <div style={{ ...styles.cardHeader, background: 'rgba(87,42,195,0.08)', borderBottom: '1px solid rgba(87,42,195,0.15)' }}>
                            <span style={styles.cardHeaderIcon}>📤</span>
                            <span style={{ ...styles.cardHeaderTitle, color: '#7a5cd4' }}>Как отправить отчёт разработчику</span>
                        </div>
                        <div style={{ ...styles.cardBody, lineHeight: 1.9, fontSize: 13, color: '#888' }}>
                            <p style={{ margin: '0 0 8px' }}>
                                <strong style={{ color: '#9980e0' }}>Вариант 1 (быстрый):</strong> Нажмите «Скопировать отчёт» → вставьте в Telegram/мессенджер разработчику.
                            </p>
                            <p style={{ margin: 0 }}>
                                <strong style={{ color: '#9980e0' }}>Вариант 2 (полный):</strong> Сделайте скриншот <em>всей страницы</em> — на нём будет вся необходимая информация.
                            </p>
                        </div>
                    </div>

                    {/* Кнопки */}
                    <div style={styles.buttonRow}>
                        <button
                            className="geb-btn"
                            style={styles.btnPrimary}
                            onClick={() => this.copyReport()}
                        >
                            {copied ? '✅ Скопировано!' : '📋 Скопировать отчёт'}
                        </button>
                        <button
                            className="geb-btn"
                            style={styles.btnPrimary}
                            onClick={() => window.location.reload()}
                        >
                            🔄 Перезагрузить приложение
                        </button>
                        {onReset && (
                            <button
                                className="geb-btn-sec"
                                style={styles.btnSecondary}
                                onClick={onReset}
                            >
                                ↩ Попробовать продолжить
                            </button>
                        )}
                    </div>

                    {copied && (
                        <div style={styles.copySuccess}>
                            ✓ Отчёт скопирован в буфер обмена
                        </div>
                    )}

                </div>
            </div>
        );
    }
}


// ─── Глобальный ErrorBoundary ──────────────────────────────────────────────────

export class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            meta: null,
        };
        this.handleReset = this.handleReset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        const meta = {
            timestamp: getTimestamp(),
            url: window.location.href,
            browser: getBrowserInfo(),
            device: getDeviceInfo(),
            os: getOSInfo(),
            screen: `${window.screen.width}×${window.screen.height} (окно: ${window.innerWidth}×${window.innerHeight})`,
        };

        this.setState({ error, errorInfo, meta });

        // Логируем в консоль для devtools
        console.error('🚨 [GlobalErrorBoundary] Поймана ошибка:', error);
        console.error('📋 Component stack:', errorInfo?.componentStack);
        console.error('🖥️ Meta:', meta);
    }

    handleReset() {
        this.setState({ hasError: false, error: null, errorInfo: null, meta: null });
    }

    render() {
        if (this.state.hasError && this.state.error) {
            return (
                <ErrorPage
                    error={this.state.error}
                    errorInfo={this.state.errorInfo}
                    meta={this.state.meta}
                    onReset={this.handleReset}
                />
            );
        }
        return this.props.children;
    }
}


// ─── Локальный ErrorBoundary (для модалок и отдельных зон) ───────────────────

export class LocalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('🔴 [LocalErrorBoundary]', this.props.name || '', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) return this.props.fallback;
            return (
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    background: 'rgba(255,59,48,0.06)',
                    border: '1px solid rgba(255,59,48,0.2)',
                    borderRadius: '12px',
                    margin: '8px',
                    fontFamily: 'inherit',
                }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
                    <div style={{ fontWeight: 700, color: '#ff3b30', marginBottom: 4, fontSize: 14 }}>
                        {this.props.name ? `Ошибка в "${this.props.name}"` : 'Ошибка компонента'}
                    </div>
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
                        Этот блок не загрузился. Остальное приложение работает.
                    </div>
                    <button
                        onClick={() => this.setState({ hasError: false })}
                        style={{
                            background: 'transparent',
                            border: '1px solid #333',
                            borderRadius: '6px',
                            padding: '6px 14px',
                            color: '#888',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontFamily: 'inherit',
                        }}
                    >
                        Попробовать снова
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}